import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, CheckCircle2, Circle, Clock, AlertTriangle, ClipboardList, PlayCircle, User, ExternalLink, BookOpen, Pencil, Trash2, Plus, X } from "lucide-react";

const fmt = (iso, opts = { day: "2-digit", month: "short", year: "numeric" }) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", opts); }
  catch { return iso; }
};

const dtFmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const today = new Date().toISOString().slice(0, 10);

function SeverityBadge({ severity }) {
  if (!severity) return null;
  const meta = {
    // Legacy 3-level vocab (still used by other workflows).
    must:   { label: "MUST",   tone: "bg-brand-accent/10 text-brand-accent border-brand-accent/30", title: "Statutory / contractual requirement" },
    should: { label: "SHOULD", tone: "bg-brand-linen text-brand-secondary border-brand-linen",       title: "Recommended good practice" },
    review: { label: "REVIEW", tone: "bg-amber-50 text-amber-800 border-amber-200",                  title: "Flagged for administrator review" },
    // New 4-level taxonomy used by the sickness workflow onwards.
    statutory:                { label: "STATUTORY",       tone: "bg-brand-accent/10 text-brand-accent border-brand-accent/30", title: "Legal / statutory requirement" },
    good_practice:            { label: "GOOD PRACTICE",   tone: "bg-brand-linen text-brand-secondary border-brand-linen",       title: "Recommended by national guidance (ACAS / GOV.UK / NHS Scotland)" },
    practice_policy:          { label: "PRACTICE POLICY", tone: "bg-emerald-50 text-emerald-800 border-emerald-200",             title: "Depends on your practice's own written policy" },
    management_consideration: { label: "MANAGEMENT",      tone: "bg-amber-50 text-amber-800 border-amber-200",                   title: "Management consideration — your judgement call" },
  };
  const m = meta[severity] || { label: severity.toUpperCase(), tone: meta.should.tone, title: severity };
  return (
    <span
      className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border whitespace-nowrap ${m.tone}`}
      title={m.title}
    >
      {m.label}
    </span>
  );
}

function TaskCard({ task, onToggle, onEdit, canEdit, busy }) {
  const done = task.status === "complete";
  const overdue = !done && task.due_date && task.due_date < today;
  return (
    <div
      data-testid={`po-instance-task-${task.id}`}
      className={`rounded-2xl border p-4 flex items-start gap-3 ${
        done ? "bg-brand-linen/30 border-brand-linen"
             : overdue ? "bg-brand-accent/5 border-brand-accent/30"
             : "bg-white border-brand-linen"
      }`}
    >
      <button
        disabled={busy}
        onClick={() => onToggle(task)}
        data-testid={`po-task-toggle-${task.id}`}
        className="mt-0.5 flex-shrink-0 text-brand-ink hover:opacity-80 disabled:opacity-50 transition-opacity"
        aria-label={done ? "Mark incomplete" : "Mark complete"}
      >
        {done
          ? <CheckCircle2 className="w-5 h-5 text-emerald-600" strokeWidth={1.75} />
          : <Circle className="w-5 h-5 text-brand-muted" strokeWidth={1.5} />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2 flex-wrap">
          <div className={`text-sm ${done ? "text-brand-muted line-through" : "text-brand-ink"} flex-1 min-w-0`}>{task.title}</div>
          {task.custom && (
            <span className="text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border border-emerald-200 bg-emerald-50 text-emerald-800" title="Added for this practice">
              CUSTOM
            </span>
          )}
          <SeverityBadge severity={task.severity} />
          {canEdit && !done && (
            <button
              onClick={() => onEdit(task)}
              data-testid={`po-task-edit-${task.id}`}
              className="p-1 rounded-full text-brand-muted hover:text-brand-ink hover:bg-brand-linen transition-colors"
              aria-label="Edit task"
              title="Edit this task for this practice (master template unchanged)"
              disabled={busy}
            >
              <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
          )}
        </div>
        {task.guidance && !done && (
          <div className="text-xs text-brand-body mt-1 leading-relaxed">{task.guidance}</div>
        )}
        <div className="text-[11px] text-brand-muted mt-1.5 flex flex-wrap items-center gap-3">
          {task.assign_role && <span className="uppercase tracking-widest">{task.assign_role}</span>}
          {task.due_date && (
            <span className={overdue ? "text-brand-accent font-medium" : ""}>
              Due {fmt(task.due_date)}
            </span>
          )}
          {task.source_url && (
            <a
              href={task.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-brand-secondary hover:text-brand-ink underline decoration-dotted underline-offset-2"
              onClick={(e) => e.stopPropagation()}
              data-testid={`po-task-source-${task.id}`}
            >
              <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
              Guidance source
            </a>
          )}
          {task.edited_by && (
            <span className="text-emerald-700" title={`Edited by ${task.edited_by}`}>
              Adjusted for this practice
            </span>
          )}
          {done && task.completed_by && (
            <span>Completed by {task.completed_by} · {dtFmt(task.completed_at)}</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PracticeOpsInstance() {
  const { instanceId } = useParams();
  const { user } = useAuth();
  const canEdit = user?.role === "admin";
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);   // task being edited
  const [adding, setAdding] = useState(null);     // { stage_id }

  const load = async () => {
    setErr("");
    try {
      const res = await api.get(`/practice-ops/instances/${instanceId}`);
      setData(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load workflow");
    }
  };

  useEffect(() => { load(); }, [instanceId]);

  const toggleTask = async (task) => {
    setBusy(true);
    try {
      const nextStatus = task.status === "complete" ? "pending" : "complete";
      await api.patch(`/practice-ops/tasks/${task.id}`, { status: nextStatus });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update task");
    } finally { setBusy(false); }
  };

  const saveEdit = async (task, changes) => {
    setBusy(true);
    try {
      await api.patch(`/practice-ops/tasks/${task.id}`, changes);
      toast.success("Task updated");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update task");
    } finally { setBusy(false); }
  };

  const removeTask = async (task) => {
    if (!window.confirm(`Remove "${task.title}" from this workflow? This affects only this run — the master template is unchanged.`)) return;
    setBusy(true);
    try {
      await api.delete(`/practice-ops/tasks/${task.id}`);
      toast.success("Task removed");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove task");
    } finally { setBusy(false); }
  };

  const addTask = async (stageId, payload) => {
    setBusy(true);
    try {
      await api.post(`/practice-ops/instances/${instanceId}/tasks`, { ...payload, stage_id: stageId });
      toast.success("Task added");
      setAdding(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add task");
    } finally { setBusy(false); }
  };

  const completeInstance = async () => {
    setBusy(true);
    try {
      await api.post(`/practice-ops/instances/${instanceId}/complete`);
      toast.success("Workflow marked complete");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not complete workflow");
    } finally { setBusy(false); }
  };

  const stagesById = useMemo(() => {
    if (!data?.definition?.stages) return {};
    const m = {};
    for (const s of data.definition.stages) m[s.id] = s;
    return m;
  }, [data]);

  const tasksByStage = useMemo(() => {
    if (!data) return {};
    const stageIdFor = {};
    for (const t of data.definition.tasks || []) stageIdFor[t.id] = t.stage_id;
    const groups = {};
    for (const t of data.tasks) {
      if (t.status === "superseded") continue; // hidden from stage lists
      // Custom PM-added tasks carry their own stage on custom_stage_id.
      const sid = t.custom_stage_id || stageIdFor[t.workflow_task_id] || "_ungrouped";
      (groups[sid] = groups[sid] || []).push(t);
    }
    return groups;
  }, [data]);

  const supersededTasks = useMemo(
    () => (data?.tasks || []).filter((t) => t.status === "superseded"),
    [data]
  );

  const saveAnswers = async (changes) => {
    setBusy(true);
    try {
      await api.patch(`/practice-ops/instances/${instanceId}/answers`, { answers: changes });
      toast.success("Workflow updated");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save answers");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout wide>
      <div className="mb-6">
        <Link to="/practice-ops" data-testid="po-instance-back"
          className="inline-flex items-center gap-1.5 text-sm text-brand-secondary hover:text-brand-ink transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
          Back to Practice Ops
        </Link>
      </div>

      {err && <div data-testid="po-instance-error" className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>}

      {data && (
        <>
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-brand-muted mb-1">
                {data.scenario_category || "Workflow"} · v{data.workflow_version}
              </div>
              <h1 className="font-serif text-4xl text-brand-ink" data-testid="po-instance-title">{data.title || data.scenario_name}</h1>
              <div className="text-sm text-brand-muted mt-2 flex flex-wrap gap-x-4 gap-y-1">
                {data.event_date && <span>Event: {fmt(data.event_date)}</span>}
                <span>Started: {dtFmt(data.started_at)}</span>
                <span>Status: <span className={data.status === "active" ? "text-brand-ink" : "text-emerald-700"}>{data.status}</span></span>
              </div>
            </div>
            {data.status === "active" && (
              <button
                disabled={busy}
                onClick={completeInstance}
                data-testid="po-instance-complete"
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-ink text-brand-bone px-4 py-2 text-sm hover:bg-brand-inkHover transition-colors disabled:opacity-50"
              >
                <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Mark workflow complete
              </button>
            )}
          </div>

          <div className="rounded-2xl border border-brand-linen bg-white p-5 mb-8">
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2 text-sm">
              <span className="text-[11px] uppercase tracking-widest text-brand-muted">Progress</span>
              <span className="font-serif text-2xl text-brand-ink">{data.progress.percent}%</span>
              <span className="text-brand-secondary">
                {data.progress.completed} of {data.progress.total} tasks complete
              </span>
              {data.progress.current_stage_id && (
                <span className="text-brand-secondary">
                  Current stage:
                  <span className="text-brand-ink ml-1">{stagesById[data.progress.current_stage_id]?.name || data.progress.current_stage_id}</span>
                </span>
              )}
            </div>
            <div className="mt-3 h-1.5 bg-brand-linen rounded-full overflow-hidden">
              <div className="h-full bg-brand-ink transition-all" style={{ width: `${data.progress.percent}%` }} />
            </div>
          </div>

          {(data.definition?.guidance_summary || (data.definition?.sources || []).length > 0) && (
            <div data-testid="po-instance-sources" className="rounded-2xl border border-brand-linen bg-brand-linen/20 p-5 mb-8">
              <div className="flex items-start gap-2 mb-2">
                <BookOpen className="w-4 h-4 text-brand-secondary mt-0.5" strokeWidth={1.5} />
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-brand-muted">Authoritative guidance sources</div>
                  {data.definition?.guidance_summary && (
                    <p className="text-sm text-brand-body mt-1 leading-relaxed">{data.definition.guidance_summary}</p>
                  )}
                </div>
              </div>
              {(data.definition?.sources || []).length > 0 && (
                <ul className="mt-3 grid gap-1.5 sm:grid-cols-2">
                  {data.definition.sources.map((s, i) => (
                    <li key={i}>
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`po-source-${i}`}
                        className="inline-flex items-start gap-1.5 text-sm text-brand-secondary hover:text-brand-ink transition-colors"
                      >
                        <ExternalLink className="w-3 h-3 mt-1 flex-shrink-0" strokeWidth={1.5} />
                        <span>
                          <span className="text-brand-ink">{s.organisation}</span>
                          {s.note && <span className="text-brand-muted"> — {s.note}</span>}
                          {s.date_checked && <span className="text-brand-muted text-xs ml-1">(checked {s.date_checked})</span>}
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-3 text-[11px] text-brand-muted">
                Tasks flagged <span className="font-medium">STATUTORY</span> are UK legal / contractual duties.
                <span className="font-medium"> GOOD PRACTICE</span> is national guidance (ACAS / GOV.UK / NHS Scotland).
                <span className="font-medium"> PRACTICE POLICY</span> depends on your own written policy.
                <span className="font-medium"> MANAGEMENT</span> is your judgement call.
                Legacy workflows may still show <span className="font-medium">MUST / SHOULD / REVIEW</span> — the meaning is equivalent.
              </div>
            </div>
          )}

          {data.summary && <SummaryBar summary={data.summary} ssp_rate={data.ssp_rate} />}
          <SspPanel ssp={data.ssp} />

          {(data.visible_questions || []).length > 0 && (
            <QuestionsPanel
              questions={data.visible_questions}
              answers={data.answers || {}}
              onSave={saveAnswers}
              busy={busy}
              readOnly={data.status !== "active" || !canEdit}
            />
          )}

          <CspPanel csp={data.csp} />

          {(data.definition.stages || []).map((stage) => {
            const stageTasks = tasksByStage[stage.id] || [];
            return (
              <section key={stage.id} data-testid={`po-stage-${stage.id}`} className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <PlayCircle className="w-4 h-4 text-brand-secondary" strokeWidth={1.5} />
                  <h2 className="font-serif text-xl text-brand-ink">{stage.name}</h2>
                  {canEdit && (
                    <button
                      onClick={() => setAdding({ stage_id: stage.id, stage_name: stage.name })}
                      data-testid={`po-stage-add-${stage.id}`}
                      className="ml-auto inline-flex items-center gap-1 text-xs text-brand-secondary hover:text-brand-ink transition-colors"
                    >
                      <Plus className="w-3 h-3" strokeWidth={1.75} /> Add task
                    </button>
                  )}
                </div>
                {stageTasks.length === 0 ? (
                  <div className="text-brand-muted text-xs italic pl-6">No tasks in this stage.</div>
                ) : (
                  <div className="grid gap-2">
                    {stageTasks.map((t) => <TaskCard key={t.id} task={t} onToggle={toggleTask} onEdit={setEditing} canEdit={canEdit} busy={busy} />)}
                  </div>
                )}
              </section>
            );
          })}

          <section className="mt-10">
            <div className="flex items-center gap-2 mb-3">
              <ClipboardList className="w-4 h-4 text-brand-secondary" strokeWidth={1.5} />
              <h2 className="font-serif text-xl text-brand-ink">Audit history</h2>
            </div>
            {data.audit.length === 0 ? (
              <div className="text-brand-muted text-sm">No events yet.</div>
            ) : (
              <div className="rounded-2xl border border-brand-linen bg-white divide-y divide-brand-linen">
                {data.audit.map((a) => (
                  <div key={a.id} className="px-4 py-3 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
                    <div className="text-brand-ink font-medium capitalize">{(a.event || "").replace(/_/g, " ")}</div>
                    {a.actor_email && <div className="text-brand-secondary text-xs">by {a.actor_email}</div>}
                    <div className="ml-auto text-brand-muted text-xs">{dtFmt(a.at)}</div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {supersededTasks.length > 0 && (
            <section className="mt-10" data-testid="po-superseded-section">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-brand-muted" strokeWidth={1.5} />
                <h2 className="font-serif text-xl text-brand-ink">No longer applicable</h2>
                <span className="text-brand-muted text-xs">
                  ({supersededTasks.length}) — hidden because current answers make them not apply. Change an answer to bring them back.
                </span>
              </div>
              <div className="rounded-2xl border border-brand-linen bg-brand-linen/20 divide-y divide-brand-linen">
                {supersededTasks.map((t) => (
                  <div key={t.id} data-testid={`po-superseded-task-${t.id}`} className="px-4 py-3 text-sm">
                    <div className="text-brand-secondary line-through">{t.title}</div>
                    <div className="text-brand-muted text-[11px] mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 items-center">
                      <SeverityBadge severity={t.severity} />
                      <span>Set aside {dtFmt(t.superseded_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {editing && (
        <TaskEditModal
          task={editing}
          onCancel={() => setEditing(null)}
          onSave={saveEdit}
          onDelete={removeTask}
          busy={busy}
        />
      )}
      {adding && (
        <TaskAddModal
          stage={adding}
          onCancel={() => setAdding(null)}
          onSave={addTask}
          busy={busy}
        />
      )}
    </Layout>
  );
}

function TaskEditModal({ task, onCancel, onSave, onDelete, busy }) {
  const [title, setTitle] = useState(task.title || "");
  const [guidance, setGuidance] = useState(task.guidance || "");
  const [dueDate, setDueDate] = useState(task.due_date || "");
  const [assignRole, setAssignRole] = useState(task.assign_role || "pm");
  const changes = {
    title: title !== (task.title || "") ? title : undefined,
    guidance: guidance !== (task.guidance || "") ? guidance : undefined,
    due_date: dueDate !== (task.due_date || "") ? dueDate : undefined,
    assign_role: assignRole !== (task.assign_role || "") ? assignRole : undefined,
  };
  const dirty = Object.values(changes).some((v) => v !== undefined);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !busy && onCancel()}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" data-testid="po-task-edit-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-brand-muted mb-1">Edit task for this workflow</div>
            <h2 className="font-serif text-xl text-brand-ink">Adjust for your practice</h2>
          </div>
          <button onClick={onCancel} className="p-1 text-brand-muted hover:text-brand-ink" aria-label="Close" disabled={busy}>
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        <p className="text-[11px] text-brand-muted mb-4">
          Changes apply to this workflow only. The master template based on national guidance is not modified.
        </p>
        <Field label="Title">
          <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="po-edit-title"
            className="w-full px-3 py-2 rounded-xl border border-brand-linen bg-white focus:outline-none focus:border-brand-ink text-sm" />
        </Field>
        <Field label="Guidance / instructions">
          <textarea value={guidance} onChange={(e) => setGuidance(e.target.value)} data-testid="po-edit-guidance" rows={4}
            className="w-full px-3 py-2 rounded-xl border border-brand-linen bg-white focus:outline-none focus:border-brand-ink text-sm" />
          <div className="text-[10px] text-brand-muted mt-1">Do NOT include patient-identifiable information.</div>
        </Field>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Field label="Due date">
            <input type="date" value={dueDate || ""} onChange={(e) => setDueDate(e.target.value)} data-testid="po-edit-due"
              className="w-full px-3 py-2 rounded-xl border border-brand-linen bg-white focus:outline-none focus:border-brand-ink text-sm" />
          </Field>
          <Field label="Assign role">
            <select value={assignRole} onChange={(e) => setAssignRole(e.target.value)} data-testid="po-edit-role"
              className="w-full px-3 py-2 rounded-xl border border-brand-linen bg-white focus:outline-none focus:border-brand-ink text-sm">
              <option value="pm">Practice Manager</option>
              <option value="gp_partner">GP Partner</option>
              <option value="clinical_lead">Clinical lead</option>
              <option value="administrator">Administrator</option>
              <option value="line_manager">Line Manager</option>
              <option value="supervisor">Supervisor</option>
              <option value="it_lead">IT lead</option>
            </select>
          </Field>
        </div>
        <div className="flex justify-between items-center pt-2 border-t border-brand-linen mt-2">
          <button onClick={() => onDelete(task)} disabled={busy} data-testid="po-edit-delete"
            className="inline-flex items-center gap-1 text-xs text-brand-accent hover:opacity-80 transition-opacity disabled:opacity-50">
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Remove this task
          </button>
          <div className="flex gap-2">
            <button onClick={onCancel} disabled={busy} className="px-4 py-2 rounded-full border border-brand-linen text-sm text-brand-secondary hover:text-brand-ink transition-colors disabled:opacity-50">Cancel</button>
            <button
              onClick={() => onSave(task, changes)}
              disabled={busy || !dirty}
              data-testid="po-edit-save"
              className="px-4 py-2 rounded-full bg-brand-ink text-brand-bone text-sm hover:bg-brand-inkHover transition-colors disabled:opacity-50">
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TaskAddModal({ stage, onCancel, onSave, busy }) {
  const [title, setTitle] = useState("");
  const [guidance, setGuidance] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [assignRole, setAssignRole] = useState("pm");
  const canSave = !!title.trim();
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !busy && onCancel()}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-lg p-6" data-testid="po-task-add-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-brand-muted mb-1">Add practice-specific task</div>
            <h2 className="font-serif text-xl text-brand-ink">New task in {stage.stage_name}</h2>
          </div>
          <button onClick={onCancel} className="p-1 text-brand-muted hover:text-brand-ink" disabled={busy} aria-label="Close">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        <p className="text-[11px] text-brand-muted mb-4">
          This task exists only in this workflow run. The master template is unchanged.
        </p>
        <Field label="Title">
          <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="po-add-title" autoFocus
            className="w-full px-3 py-2 rounded-xl border border-brand-linen bg-white focus:outline-none focus:border-brand-ink text-sm" />
        </Field>
        <Field label="Guidance (optional)">
          <textarea value={guidance} onChange={(e) => setGuidance(e.target.value)} data-testid="po-add-guidance" rows={3}
            className="w-full px-3 py-2 rounded-xl border border-brand-linen bg-white focus:outline-none focus:border-brand-ink text-sm" />
          <div className="text-[10px] text-brand-muted mt-1">Do NOT include patient-identifiable information.</div>
        </Field>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Field label="Due date (optional)">
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="po-add-due"
              className="w-full px-3 py-2 rounded-xl border border-brand-linen bg-white focus:outline-none focus:border-brand-ink text-sm" />
          </Field>
          <Field label="Assign role">
            <select value={assignRole} onChange={(e) => setAssignRole(e.target.value)} data-testid="po-add-role"
              className="w-full px-3 py-2 rounded-xl border border-brand-linen bg-white focus:outline-none focus:border-brand-ink text-sm">
              <option value="pm">Practice Manager</option>
              <option value="gp_partner">GP Partner</option>
              <option value="clinical_lead">Clinical lead</option>
              <option value="administrator">Administrator</option>
              <option value="line_manager">Line Manager</option>
              <option value="supervisor">Supervisor</option>
              <option value="it_lead">IT lead</option>
            </select>
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-brand-linen">
          <button onClick={onCancel} disabled={busy} className="px-4 py-2 rounded-full border border-brand-linen text-sm text-brand-secondary hover:text-brand-ink transition-colors disabled:opacity-50">Cancel</button>
          <button
            onClick={() => onSave(stage.stage_id, { title, guidance: guidance || null, due_date: dueDate || null, assign_role: assignRole })}
            disabled={busy || !canSave}
            data-testid="po-add-save"
            className="px-4 py-2 rounded-full bg-brand-ink text-brand-bone text-sm hover:bg-brand-inkHover transition-colors disabled:opacity-50">
            {busy ? "Adding…" : "Add task"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-3">
      <div className="text-xs uppercase tracking-widest text-brand-muted mb-1">{label}</div>
      {children}
    </label>
  );
}

function QuestionInput({ question, value, onChange, disabled }) {
  const t = question.type;
  if (t === "date") {
    return (
      <input
        type="date"
        disabled={disabled}
        value={value || ""}
        onChange={(e) => onChange(e.target.value || null)}
        data-testid={`po-q-${question.id}`}
        className="w-full px-3 py-2 rounded-xl border border-brand-linen bg-white focus:outline-none focus:border-brand-ink text-sm disabled:opacity-50"
      />
    );
  }
  if (t === "number" || t === "practice_policy_value") {
    return (
      <input
        type="number"
        disabled={disabled}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        data-testid={`po-q-${question.id}`}
        className="w-full px-3 py-2 rounded-xl border border-brand-linen bg-white focus:outline-none focus:border-brand-ink text-sm disabled:opacity-50"
      />
    );
  }
  if (t === "single_select") {
    return (
      <select
        disabled={disabled}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        data-testid={`po-q-${question.id}`}
        className="w-full px-3 py-2 rounded-xl border border-brand-linen bg-white focus:outline-none focus:border-brand-ink text-sm disabled:opacity-50"
      >
        <option value="">— select —</option>
        {(question.options || []).map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }
  if (t === "multi_select") {
    const list = Array.isArray(value) ? value : [];
    const toggle = (v) => {
      const next = list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
      onChange(next);
    };
    return (
      <div className="flex flex-wrap gap-1.5" data-testid={`po-q-${question.id}`}>
        {(question.options || []).map((o) => (
          <button
            key={o.value}
            type="button"
            disabled={disabled}
            onClick={() => toggle(o.value)}
            className={`text-xs rounded-full px-3 py-1 border transition-colors ${
              list.includes(o.value)
                ? "bg-brand-ink text-brand-bone border-brand-ink"
                : "bg-white text-brand-secondary border-brand-linen hover:border-brand-ink/40"
            } disabled:opacity-50`}
          >
            {o.label}
          </button>
        ))}
      </div>
    );
  }
  if (t === "practice_policy_bands") {
    return <BandsEditor value={value} onChange={onChange} disabled={disabled} testId={`po-q-${question.id}`} />;
  }
  return (
    <input
      type="text"
      disabled
      value="Unsupported question type"
      className="w-full px-3 py-2 rounded-xl border border-brand-linen bg-brand-linen/30 text-brand-muted text-sm"
    />
  );
}

function BandsEditor({ value, onChange, disabled, testId }) {
  const rows = Array.isArray(value) && value.length > 0
    ? [...value].sort((a, b) => (a.order || 0) - (b.order || 0))
    : [];
  const update = (i, patch) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    onChange(next);
  };
  const remove = (i) => {
    const next = rows.filter((_, idx) => idx !== i).map((r, idx) => ({ ...r, order: idx + 1 }));
    onChange(next);
  };
  const add = () => {
    const nextOrder = rows.length ? Math.max(...rows.map((r) => r.order || 0)) + 1 : 1;
    onChange([...rows, { label: "New band", days: 0, order: nextOrder }]);
  };
  const applyStandardTemplate = () => {
    onChange([
      { label: "Full pay", days: 182, order: 1 },
      { label: "Half pay", days: 182, order: 2 },
      { label: "Nil pay",  days: 0,   order: 3 },
    ]);
  };
  return (
    <div data-testid={testId} className="rounded-xl border border-brand-linen bg-white p-3">
      <div className="grid grid-cols-[1fr,7rem,4rem,2rem] gap-2 text-[10px] uppercase tracking-widest text-brand-muted mb-1 px-1">
        <div>Label</div><div>Days (0 = indefinite)</div><div>Order</div><div></div>
      </div>
      {rows.length === 0 && (
        <div className="text-xs text-brand-muted italic px-1 py-2 flex flex-wrap items-center gap-2">
          <span>No bands configured yet.</span>
          <button
            type="button"
            disabled={disabled}
            onClick={applyStandardTemplate}
            data-testid={`${testId}-apply-template`}
            className="text-brand-secondary hover:text-brand-ink underline underline-offset-2 disabled:opacity-50"
          >
            Apply standard 6-month / 6-month / Nil template
          </button>
        </div>
      )}
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[1fr,7rem,4rem,2rem] gap-2 items-center mb-1.5">
          <input
            type="text"
            disabled={disabled}
            value={r.label ?? ""}
            onChange={(e) => update(i, { label: e.target.value })}
            data-testid={`${testId}-label-${i}`}
            className="w-full px-2 py-1.5 rounded-lg border border-brand-linen bg-white focus:outline-none focus:border-brand-ink text-sm disabled:opacity-50"
          />
          <input
            type="number"
            min="0"
            disabled={disabled}
            value={r.days ?? 0}
            onChange={(e) => update(i, { days: e.target.value === "" ? 0 : Number(e.target.value) })}
            data-testid={`${testId}-days-${i}`}
            className="w-full px-2 py-1.5 rounded-lg border border-brand-linen bg-white focus:outline-none focus:border-brand-ink text-sm disabled:opacity-50"
          />
          <input
            type="number"
            min="1"
            disabled={disabled}
            value={r.order ?? i + 1}
            onChange={(e) => update(i, { order: Number(e.target.value) || i + 1 })}
            className="w-full px-2 py-1.5 rounded-lg border border-brand-linen bg-white focus:outline-none focus:border-brand-ink text-sm disabled:opacity-50"
          />
          <button
            type="button"
            disabled={disabled}
            onClick={() => remove(i)}
            aria-label="Remove band"
            className="p-1 rounded text-brand-muted hover:text-brand-accent disabled:opacity-50"
          >
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
        </div>
      ))}
      <button
        type="button"
        disabled={disabled}
        onClick={add}
        data-testid={`${testId}-add`}
        className="mt-2 inline-flex items-center gap-1 text-xs text-brand-secondary hover:text-brand-ink disabled:opacity-50"
      >
        <Plus className="w-3 h-3" strokeWidth={1.75} /> Add band
      </button>
    </div>
  );
}

function CspStat({ label, value, sub, testId }) {
  return (
    <div data-testid={testId} className="rounded-xl bg-white border border-brand-linen p-3">
      <div className="text-[10px] uppercase tracking-widest text-brand-muted">{label}</div>
      <div className="font-serif text-xl text-brand-ink mt-0.5">{value}</div>
      {sub && <div className="text-[11px] text-brand-muted mt-0.5">{sub}</div>}
    </div>
  );
}

function SspPanel({ ssp }) {
  if (!ssp || !ssp.enabled) return null;
  const alertsHaveContent = Array.isArray(ssp.alerts) && ssp.alerts.length > 0;
  const rate = ssp.rate || {};
  return (
    <div data-testid="po-ssp-panel" className="rounded-2xl border border-brand-linen bg-white p-5 mb-8">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-brand-muted">Statutory Sick Pay entitlement</div>
          <div className="font-serif text-xl text-brand-ink mt-0.5">Statutory — separate from any contractual scheme</div>
        </div>
        <span className="text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border border-brand-accent/30 bg-brand-accent/10 text-brand-accent">
          Statutory
        </span>
      </div>
      <p className="text-xs text-brand-body leading-relaxed mb-4">{ssp.disclaimer}</p>

      <div className="grid gap-3 md:grid-cols-4 mb-3">
        <CspStat testId="ssp-days-used" label="Days used" value={`${ssp.days_used} of ${ssp.max_entitlement_days}`} sub={`${ssp.max_entitlement_weeks} weeks max entitlement`} />
        <CspStat testId="ssp-days-remaining" label="Days remaining" value={ssp.exhausted ? "0 (exhausted)" : ssp.days_remaining} sub={ssp.payable_now ? "Payable now" : `From day ${ssp.payable_from_day}`} />
        <CspStat testId="ssp-rate" label="Applicable rate" value={rate.weekly_rate_gbp != null ? `£${Number(rate.weekly_rate_gbp).toFixed(2)}/wk` : "—"} sub={rate.percent_awe ? `cap at ${rate.percent_awe}% AWE · effective ${fmt(rate.effective_from)}` : `effective ${fmt(rate.effective_from)}`} />
        <CspStat testId="ssp-exhaustion" label="Exhaustion date" value={fmt(ssp.exhaustion_date)} sub={`SSP1 window opens ${fmt(ssp.ssp1_earliest_date)}`} />
      </div>

      {alertsHaveContent && (
        <div className="rounded-xl border border-brand-accent/30 bg-brand-accent/5 p-3" data-testid="ssp-alerts">
          <div className="text-[11px] uppercase tracking-widest text-brand-accent mb-1.5 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.5} /> Statutory alerts
          </div>
          <ul className="text-sm text-brand-ink space-y-1">
            {ssp.alerts.map((a, i) => (
              <li key={i} data-testid={`ssp-alert-${a.type}-${i}`}>{a.message}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function CspPanel({ csp }) {
  if (!csp || !csp.enabled) return null;
  const cb = csp.current_band;
  const nb = csp.next_band;
  if (csp.unconfigured) {
    return (
      <div data-testid="po-csp-panel" className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5 mb-8">
        <div className="flex items-baseline justify-between gap-2 mb-1">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-amber-800/80">Contractual sick pay projection</div>
            <div className="font-serif text-xl text-brand-ink mt-0.5">Not configured</div>
          </div>
          <span data-testid="csp-unconfigured-badge" className="text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border border-amber-200 bg-amber-50 text-amber-800">
            Practice policy — please configure
          </span>
        </div>
        <p className="text-xs text-brand-body leading-relaxed">{csp.disclaimer}</p>
      </div>
    );
  }
  return (
    <div data-testid="po-csp-panel" className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5 mb-8">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-emerald-800/80">Contractual sick pay projection</div>
          <div className="font-serif text-xl text-brand-ink mt-0.5">Practice scheme — not a statutory rule</div>
        </div>
        <span className="text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border border-emerald-200 bg-emerald-50 text-emerald-800">
          Practice policy
        </span>
      </div>
      <p className="text-xs text-brand-body leading-relaxed mb-4">{csp.disclaimer}</p>

      <div className="grid gap-3 md:grid-cols-4 mb-4">
        <CspStat testId="csp-duration"     label="Absence duration"    value={`${csp.absence_days_this_period} day${csp.absence_days_this_period === 1 ? "" : "s"}`} sub={csp.reference_is_return ? `to return ${fmt(csp.reference_date)}` : `to ${fmt(csp.reference_date)}`} />
        <CspStat testId="csp-effective"    label="Effective days used" value={csp.effective_days_used} sub={csp.lookback_enabled ? `incl. ${csp.previous_days_counted} previous` : "this absence only"} />
        <CspStat testId="csp-current-band" label="Current band"        value={cb ? cb.label : (csp.exhausted ? "Exhausted" : "—")} sub={cb && cb.end_date ? `ends ${fmt(cb.end_date)}` : (cb && cb.indefinite ? "indefinite" : null)} />
        <CspStat testId="csp-next-band"    label="Next band"           value={nb ? nb.label : "—"} sub={nb && nb.start_date ? `from ${fmt(nb.start_date)}` : null} />
      </div>

      {csp.exhaustion_date && (
        <div className="text-sm text-brand-ink mb-3" data-testid="csp-exhaustion-date">
          <span className="text-brand-muted uppercase tracking-widest text-[10px] mr-2">Contractual pay exhausts</span>
          <span className="font-medium">{fmt(csp.exhaustion_date)}</span>
        </div>
      )}

      {csp.alerts.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 mb-3" data-testid="csp-alerts">
          <div className="text-[11px] uppercase tracking-widest text-amber-800 mb-1.5 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.5} /> Advance alerts (within {csp.alert_lead_days} days)
          </div>
          <ul className="text-sm text-brand-ink space-y-1">
            {csp.alerts.map((a, i) => (
              <li key={i} data-testid={`csp-alert-${a.type}-${i}`}>
                {a.type === "exhaustion_soon"
                  ? `Contractual pay entitlement exhausts on ${fmt(a.date)} (in ${a.days_until} day${a.days_until === 1 ? "" : "s"})`
                  : `Pay changes to "${a.band_label}" on ${fmt(a.date)} (in ${a.days_until} day${a.days_until === 1 ? "" : "s"})`}
              </li>
            ))}
          </ul>
        </div>
      )}

      {csp.payroll_actions.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-widest text-brand-muted mb-1.5">Payroll action dates</div>
          <ul className="text-sm text-brand-ink divide-y divide-brand-linen border border-brand-linen rounded-xl bg-white overflow-hidden">
            {csp.payroll_actions.map((p, i) => (
              <li key={i} className="px-3 py-2 flex items-center justify-between" data-testid={`csp-payroll-${i}`}>
                <span>{p.action}</span>
                <span className="text-brand-secondary text-sm">{fmt(p.date)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function QuestionsPanel({ questions, answers, onSave, busy, readOnly }) {
  const [draft, setDraft] = useState({});
  // Reset the draft whenever the underlying answers change (e.g. after save/reload).
  useEffect(() => { setDraft({}); }, [answers]);

  const merged = { ...answers, ...draft };
  const dirty = Object.keys(draft).length > 0;
  const set = (id, v) => setDraft((d) => ({ ...d, [id]: v }));

  return (
    <div data-testid="po-questions-panel" className="rounded-2xl border border-brand-linen bg-white p-5 mb-8">
      <div className="flex items-baseline justify-between gap-2 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-brand-muted mb-0.5">Workflow questions</div>
          <div className="text-brand-secondary text-sm">
            Answer these as the situation develops — tasks below adjust automatically.
          </div>
        </div>
        {readOnly && <span className="text-[11px] text-brand-muted italic">Read-only</span>}
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {questions.map((q) => (
          <div key={q.id}>
            <div className="flex items-baseline gap-2 mb-1 flex-wrap">
              <div className="text-xs uppercase tracking-widest text-brand-muted">{q.label}</div>
              {(q.type === "practice_policy_value" || q.practice_policy) && (
                <span className="text-[10px] uppercase tracking-widest rounded-full px-1.5 border border-emerald-200 bg-emerald-50 text-emerald-800">
                  Practice policy
                </span>
              )}
              {(q.type === "practice_policy_value" || q.practice_policy) && (merged[q.id] === undefined || merged[q.id] === null || merged[q.id] === "") && (
                <span data-testid={`po-q-${q.id}-not-configured`} className="text-[10px] uppercase tracking-widest rounded-full px-1.5 border border-amber-200 bg-amber-50 text-amber-800">
                  Not configured — please set
                </span>
              )}
              {q.required && <span className="text-[10px] text-brand-accent">required</span>}
            </div>
            <QuestionInput
              question={q}
              value={
                merged[q.id] !== undefined
                  ? merged[q.id]
                  : ((q.type === "practice_policy_value" || q.practice_policy) ? null : (q.default ?? null))
              }
              onChange={(v) => set(q.id, v)}
              disabled={readOnly || busy}
            />
            {q.help && <div className="text-[11px] text-brand-muted mt-1">{q.help}</div>}
          </div>
        ))}
      </div>
      {!readOnly && (
        <div className="flex justify-end mt-4 pt-3 border-t border-brand-linen">
          <button
            data-testid="po-questions-save"
            disabled={busy || !dirty}
            onClick={() => onSave(draft)}
            className="px-4 py-2 rounded-full bg-brand-ink text-brand-bone text-sm hover:bg-brand-inkHover transition-colors disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save answers"}
          </button>
        </div>
      )}
    </div>
  );
}


function SummaryBar({ summary, ssp_rate }) {
  if (!summary) return null;
  const tile = (testId, label, value, sub) => (
    <div data-testid={testId} className="rounded-xl bg-white border border-brand-linen p-3">
      <div className="text-[10px] uppercase tracking-widest text-brand-muted">{label}</div>
      <div className="font-serif text-lg text-brand-ink mt-0.5 leading-tight">{value}</div>
      {sub && <div className="text-[11px] text-brand-muted mt-0.5">{sub}</div>}
    </div>
  );

  // Current absence day
  const dayValue = summary.has_returned
    ? `Returned ${summary.return_date ? fmt(summary.return_date) : ""}`
    : (summary.elapsed_days ? `Day ${summary.elapsed_days}` : "—");
  const daySub = summary.event_date ? `from ${fmt(summary.event_date)}` : null;

  // SSP status
  let sspValue = "—";
  let sspSub = null;
  if (summary.ssp) {
    const rateStr = summary.ssp.weekly_rate_gbp != null ? `£${Number(summary.ssp.weekly_rate_gbp).toFixed(2)}/wk` : "";
    const pct = summary.ssp.percent_awe ? `${summary.ssp.percent_awe}% AWE cap ` : "";
    if (summary.ssp.exhausted) {
      sspValue = "Exhausted";
    } else if (summary.ssp.ssp1_due_now) {
      sspValue = "Issue SSP1 now";
    } else if (summary.ssp.days_used != null && summary.ssp.max_entitlement_days) {
      sspValue = `Day ${summary.ssp.days_used} of ${summary.ssp.max_entitlement_days}`;
    } else {
      sspValue = summary.ssp.payable_now ? "Payable (statutory)" : `From day ${summary.ssp.payable_from_day}`;
    }
    const remainStr = summary.ssp.days_remaining != null ? `${summary.ssp.days_remaining} days remaining · ` : "";
    sspSub = `${remainStr}${pct}${rateStr}${summary.ssp.lel_removed ? " · no LEL" : ""}`.trim();
  }

  // CSP status
  let cspValue = summary.csp_configured ? (summary.csp?.label || "—") : "Not configured";
  let cspSub = summary.csp_configured && summary.csp?.end_date
    ? `ends ${fmt(summary.csp.end_date)}`
    : (summary.csp_configured && summary.csp?.indefinite ? "indefinite" : "Practice policy");

  // Next action
  const nextAction = summary.next_action
    ? { value: summary.next_action.title, sub: summary.next_action.due_date ? `due ${fmt(summary.next_action.due_date)}` : null }
    : { value: "—", sub: null };

  // Next pay change
  const nextPay = summary.next_pay_change
    ? { value: summary.next_pay_change.action, sub: fmt(summary.next_pay_change.date) }
    : { value: "—", sub: summary.csp_configured ? "no upcoming change" : "no scheme configured" };

  // Next review
  const nextReview = summary.next_review
    ? { value: fmt(summary.next_review.date), sub: summary.next_review.label }
    : { value: "—", sub: "long-term review not configured" };

  return (
    <div data-testid="po-summary-bar" className="rounded-2xl border border-brand-linen bg-brand-linen/30 p-4 mb-8">
      <div className="text-[11px] uppercase tracking-widest text-brand-muted mb-3 flex items-center gap-2">
        <Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> Workflow status
        {summary.as_of && <span className="text-brand-muted">as of {fmt(summary.as_of)}</span>}
      </div>
      <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-6">
        {tile("summary-day", "Current absence day", dayValue, daySub)}
        {tile("summary-ssp", "SSP status", sspValue, sspSub)}
        {tile("summary-csp", "Contractual sick pay", cspValue, cspSub)}
        {tile("summary-next-action", "Next required action", <span title={nextAction.value} className="line-clamp-2 block">{nextAction.value}</span>, nextAction.sub)}
        {tile("summary-next-pay", "Next pay change", nextPay.value, nextPay.sub)}
        {tile("summary-next-review", "Next review", nextReview.value, nextReview.sub)}
      </div>
      {ssp_rate && (
        <div className="text-[11px] text-brand-muted mt-3">
          Statutory rate applicable to this absence: <span className="font-medium">£{Number(ssp_rate.weekly_rate_gbp).toFixed(2)}/week</span>
          {ssp_rate.percent_awe ? ` (cap at ${ssp_rate.percent_awe}% AWE)` : ""}
          {" · effective from "}{fmt(ssp_rate.effective_from)}
          {ssp_rate.source && (
            <>
              {" · "}
              <a href={ssp_rate.source} target="_blank" rel="noopener noreferrer" className="underline hover:text-brand-ink">source</a>
            </>
          )}
        </div>
      )}
    </div>
  );
}
